import * as Sentry from '@sentry/astro';
import { stage as environment } from './src/utils/endpoint';
import { isAnonymousStackEvent } from './src/utils/sentry-filters';

const isProduction = environment === 'production';
const isStaging = environment === 'staging';

const tracesSampleRate = isProduction ? 1.0 : isStaging ? 0.5 : 0.1;
const replaysSessionSampleRate = isProduction ? 0.1 : isStaging ? 0.05 : 0;
const replaysOnErrorSampleRate = isProduction ? 0.5 : isStaging ? 0.25 : 0;

const initSentry = () => {
  Sentry.init({
    dsn: import.meta.env.PUBLIC_SENTRY_DSN,
    environment,
    debug: false,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,

    beforeSend(event) {
      return isAnonymousStackEvent(event) ? null : event;
    },

    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: url => {
          try {
            const hostname = new URL(url).hostname;
            return hostname !== 'stream.mux.com' && hostname !== 'image.mux.com';
          } catch {
            return true;
          }
        }
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      }),
      Sentry.captureConsoleIntegration({
        levels: ['warn', 'error']
      })
    ],

    ignoreErrors: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // why: transient Firestore gRPC errors the Firebase SDK logs via console.error and retries internally.
      /GrpcConnection RPC .* stream .* error/,
      /Received RST_STREAM/,
      /@firebase\/firestore:.*INTERNAL/,
      // why: injected by browser extensions and third-party scripts, not our code.
      /locatorjs/i,
      /grammarly/i,
      /__gCrWeb/,
      /instantSearchSDKJSBridgeClear498712/,
      /mypolygon_/,
      /celoExtensionHandler/,
      /googletagmanager.*Cannot read properties of undefined/,
      // why: browser extensions build invalid selectors from our Tailwind arbitrary-value classes (e.g. w-[200px]); SEQUEL-WEBSITE-J9.
      /Failed to execute 'querySelector' on 'Document': '[^']*[a-zA-Z0-9_-]+-\[[^\]]+\][^']*' is not a valid selector/,
      // why: the Sentry SDK logs this itself and captureConsoleIntegration re-captures it.
      'An event processor returned `null`, will not send event',
      // why: browser autoplay policy rejections, Safari and Chrome wordings (ENG-3463).
      /NotAllowedError.*(play method is not allowed|interact with the document)/,
      // why: Video.js HLS sets responseType='arraybuffer', so reading responseText throws.
      /InvalidStateError.*responseText.*XMLHttpRequest/i,
      // why: failures inside the Chromium/WebKit/Gecko media pipeline; the players already show fallback UI.
      /MediaError.*PipelineStatus::/,
      /PipelineStatus::(DEMUXER_ERROR|DECODER_ERROR|PIPELINE_ERROR)/,
      /ChunkDemuxer/,
      // why: @mux/playback-core console.errors when it cannot infer a media type from a src (SEQUEL-WEBSITE-JA).
      /Media type .* is an unrecognized or unsupported type for src/,
      // why: @mux/playback-core logs one fatal HLS error as three console calls (SEQUEL-WEBSITE-C5/C6/F5/F6); MuxVideo.astro already hides the player on error.
      /getErrorFromHlsErrorData/,
      /MediaError data:/,
      /A media error caused playback to be aborted/,
      // why: we have no Next.js router, so this is most likely another site sending
      // to our public DSN (SEQUEL-WEBSITE-K4, ENG-7966).
      /passed to next\/router in page:/,
      // why: third-party scripts or extensions reject promises with a CustomEvent, which has no stack (ENG-8093, SEQUEL-WEBSITE-K8).
      // https://linear.app/sequel/issue/ENG-8093
      /^Event `CustomEvent` \(type=[^)]*\) captured as promise rejection$/
    ]
  });
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(initSentry);
} else {
  setTimeout(initSentry, 2000);
}
